import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API, getAuthHeaders } from '../shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Edit, Trash2, ChevronDown, ChevronRight, Archive, Building2, BedDouble, Calendar, AlertTriangle } from 'lucide-react';

const HierarchicalServicesManagement = () => {
  const { user } = useContext(AuthContext);
  const canManage = user?.role === 'super_admin' || user?.role === 'general_accountant' || user?.role === 'general_manager' || user?.role === 'services_manager';
  // 🆕 2026-07-22 — services_manager can now EDIT (including prices) and
  // DELETE hierarchical entries. Season-wide archive/reset stays super_admin.
  const canEditDelete = user?.role === 'super_admin' || user?.role === 'general_accountant' || user?.role === 'general_manager' || user?.role === 'services_manager';

  const [services, setServices] = useState([]);
  const [expanded, setExpanded] = useState({});      // {serviceId: true}
  const [hotels, setHotels] = useState({});          // {serviceId: [hotels]}
  const [rooms, setRooms] = useState({});            // {hotelId: [rooms]}
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [serviceDialog, setServiceDialog] = useState({ open: false, editing: null });
  const [hotelDialog, setHotelDialog] = useState({ open: false, serviceId: null, editing: null });
  const [roomDialog, setRoomDialog] = useState({ open: false, hotelId: null, editing: null });
  const [archiveDialog, setArchiveDialog] = useState(false);

  // Forms
  const [sForm, setSForm] = useState({ name: '', service_type: 'عمرة', departure_date: '', return_date: '', description: '', origin: 'internal', supplier_id: '', supplier_cost: '', age_pricing_enabled: false, age_pricing: null });
  const [hForm, setHForm] = useState({ name: '', city: '', notes: '' });
  const [rForm, setRForm] = useState({ room_type: '', price: '', meal_price: '', notes: '' });
  const [archiveLabel, setArchiveLabel] = useState('');
  // 🆕 Supplier picker source (loaded once)
  const [suppliers, setSuppliers] = useState([]);

  // ---- Load ----
  const fetchServices = async () => {
    try {
      const r = await axios.get(`${API}/hierarchical-services`, { headers: getAuthHeaders() });
      setServices(r.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  // 🆕 Load active suppliers (once) for the origin picker
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/suppliers?status=active`, { headers: getAuthHeaders() });
        setSuppliers(r.data || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const fetchHotels = async (serviceId) => {
    const r = await axios.get(`${API}/hierarchical-services/hotels/by-service/${serviceId}`, { headers: getAuthHeaders() });
    setHotels(h => ({ ...h, [serviceId]: r.data || [] }));
  };

  const fetchRooms = async (hotelId) => {
    const r = await axios.get(`${API}/hierarchical-services/rooms/by-hotel/${hotelId}`, { headers: getAuthHeaders() });
    setRooms(rs => ({ ...rs, [hotelId]: r.data || [] }));
  };

  useEffect(() => { fetchServices(); }, []);

  const toggleService = async (sid) => {
    const next = !expanded[sid];
    setExpanded(e => ({ ...e, [sid]: next }));
    if (next && !hotels[sid]) await fetchHotels(sid);
  };

  const toggleHotel = async (hid) => {
    const next = !expanded[hid];
    setExpanded(e => ({ ...e, [hid]: next }));
    if (next && !rooms[hid]) await fetchRooms(hid);
  };

  // ---- Service CRUD ----
  // 🆕 Age-pricing helpers (mirrors backend tiers)
  const AGE_TIERS = ['adult','teen','child','young_child_with_seat','young_child_no_seat','infant'];
  const AGE_TIER_LABEL = {
    adult:                 { label: '👤 بالغ (+16)',   range: '16+' },
    teen:                  { label: '🧑 مراهق (11-15)', range: '11-15' },
    child:                 { label: '🧒 طفل (6-10)',    range: '6-10' },
    young_child_with_seat: { label: '👶 3-5 مع كرسي',   range: '3-5 مع كرسي' },
    young_child_no_seat:   { label: '👶 3-5 بدون كرسي', range: '3-5 بدون كرسي (مجاناً)' },
    infant:                { label: '👶 رضيع (0-2)',    range: '0-2 (مجاناً)' },
  };
  const emptyAgePricing = () => ({
    adult:                 { sell: '', cost: '' },
    teen:                  { sell: '', cost: '' },
    child:                 { sell: '', cost: '' },
    young_child_with_seat: { sell: '', cost: '' },
    young_child_no_seat:   { sell: 0,  cost: 0  },
    infant:                { sell: 0,  cost: 0  },
  });

  const openServiceDialog = (editing = null) => {
    if (editing) setSForm({
      name: editing.name, service_type: editing.service_type,
      departure_date: editing.departure_date || '', return_date: editing.return_date || '',
      description: editing.description || '',
      // 🆕 supplier metadata
      origin: editing.origin || 'internal',
      supplier_id: editing.supplier_id || '',
      supplier_cost: editing.supplier_cost != null ? String(editing.supplier_cost) : '',
      // 🆕 age-based pricing preset
      age_pricing_enabled: !!editing.age_pricing,
      age_pricing: editing.age_pricing || emptyAgePricing(),
    });
    else setSForm({ name: '', service_type: 'عمرة', departure_date: '', return_date: '', description: '', origin: 'internal', supplier_id: '', supplier_cost: '', age_pricing_enabled: false, age_pricing: emptyAgePricing() });
    setServiceDialog({ open: true, editing });
  };

  const saveService = async () => {
    if (!sForm.name.trim()) { alert('أدخل اسم الخدمة'); return; }
    if (sForm.origin === 'supplier' && !sForm.supplier_id) { alert('اختر المورد'); return; }
    try {
      // 🆕 Normalise age_pricing so backend gets numbers (or null when disabled)
      let ap = null;
      if (sForm.age_pricing_enabled && sForm.age_pricing) {
        ap = {};
        for (const [tier, v] of Object.entries(sForm.age_pricing)) {
          ap[tier] = {
            sell: parseFloat(v?.sell) || 0,
            cost: parseFloat(v?.cost) || 0,
          };
        }
      }
      const { age_pricing_enabled: _apEnabled, ...formBase } = sForm;
      void _apEnabled;
      const payload = {
        ...formBase,
        supplier_cost: sForm.supplier_cost !== '' ? parseFloat(sForm.supplier_cost) : null,
        // For internal services, nullify supplier fields
        supplier_id: sForm.origin === 'supplier' ? sForm.supplier_id : null,
        age_pricing: ap,
      };
      if (sForm.origin !== 'supplier') payload.supplier_cost = null;
      if (serviceDialog.editing) {
        await axios.put(`${API}/hierarchical-services/${serviceDialog.editing.id}`, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/hierarchical-services`, payload, { headers: getAuthHeaders() });
      }
      setServiceDialog({ open: false, editing: null });
      fetchServices();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ في الحفظ'); }
  };

  const deleteService = async (id) => {
    if (!window.confirm('سيتم حذف الخدمة وجميع الفنادق والغرف المرتبطة بها. متابعة؟')) return;
    await axios.delete(`${API}/hierarchical-services/${id}`, { headers: getAuthHeaders() });
    fetchServices();
  };

  // ---- Hotel CRUD ----
  const openHotelDialog = (serviceId, editing = null) => {
    if (editing) setHForm({ name: editing.name, city: editing.city || '', notes: editing.notes || '' });
    else setHForm({ name: '', city: '', notes: '' });
    setHotelDialog({ open: true, serviceId, editing });
  };

  const saveHotel = async () => {
    if (!hForm.name.trim()) { alert('أدخل اسم الفندق'); return; }
    try {
      if (hotelDialog.editing) {
        await axios.put(`${API}/hierarchical-services/hotels/${hotelDialog.editing.id}`, hForm, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/hierarchical-services/hotels`, { ...hForm, service_id: hotelDialog.serviceId }, { headers: getAuthHeaders() });
      }
      setHotelDialog({ open: false, serviceId: null, editing: null });
      fetchHotels(hotelDialog.serviceId);
    } catch (e) { alert(e.response?.data?.detail || 'خطأ في الحفظ'); }
  };

  const deleteHotel = async (serviceId, hotelId) => {
    if (!window.confirm('سيتم حذف الفندق وجميع غرفه. متابعة؟')) return;
    await axios.delete(`${API}/hierarchical-services/hotels/${hotelId}`, { headers: getAuthHeaders() });
    fetchHotels(serviceId);
  };

  // ---- Room CRUD ----
  const openRoomDialog = (hotelId, editing = null) => {
    if (editing) setRForm({
      room_type: editing.room_type, price: String(editing.price),
      meal_price: String(editing.meal_price || ''), notes: editing.notes || ''
    });
    else setRForm({ room_type: '', price: '', meal_price: '', notes: '' });
    setRoomDialog({ open: true, hotelId, editing });
  };

  const saveRoom = async () => {
    if (!rForm.room_type.trim() || !rForm.price) { alert('أدخل نوع الغرفة والسعر'); return; }
    const payload = {
      room_type: rForm.room_type,
      price: parseFloat(rForm.price),
      meal_price: rForm.meal_price ? parseFloat(rForm.meal_price) : 0,
      notes: rForm.notes
    };
    try {
      if (roomDialog.editing) {
        await axios.put(`${API}/hierarchical-services/rooms/${roomDialog.editing.id}`, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/hierarchical-services/rooms`, { ...payload, hotel_id: roomDialog.hotelId }, { headers: getAuthHeaders() });
      }
      setRoomDialog({ open: false, hotelId: null, editing: null });
      fetchRooms(roomDialog.hotelId);
    } catch (e) { alert(e.response?.data?.detail || 'خطأ في الحفظ'); }
  };

  const deleteRoom = async (hotelId, roomId) => {
    if (!window.confirm('حذف الغرفة؟')) return;
    await axios.delete(`${API}/hierarchical-services/rooms/${roomId}`, { headers: getAuthHeaders() });
    fetchRooms(hotelId);
  };

  // ---- Archive ----
  const [archiving, setArchiving] = useState(false);
  const [keepUnpaid, setKeepUnpaid] = useState(false);
  const runArchive = async () => {
    setArchiving(true);
    try {
      const r = await axios.post(`${API}/hierarchical-services/archive-season`, null, {
        headers: getAuthHeaders(),
        params: {
          ...(archiveLabel ? { season_label: archiveLabel } : {}),
          keep_unpaid: keepUnpaid,
        }
      });
      const c = r.data.counts;
      const keptLine = (c.organized_trips_kept_unpaid && c.organized_trips_kept_unpaid > 0)
        ? `\n• تم نقل الحجوزات غير المدفوعة للموسم الجديد: ${c.organized_trips_kept_unpaid}`
        : '';
      alert(`✅ تمت الأرشفة بنجاح:\n\n• الخدمات: ${c.hierarchical_services}\n• الفنادق: ${c.service_hotels}\n• الغرف: ${c.service_rooms}\n• الرحلات المؤرشفة: ${c.organized_trips}${keptLine}\n\nالتسمية: ${c.label}`);
      setArchiveDialog(false);
      setArchiveLabel('');
      setKeepUnpaid(false);
      fetchServices();
    } catch (e) {
      alert('❌ فشلت الأرشفة: ' + (e.response?.data?.detail || e.message || 'خطأ غير معروف'));
    } finally {
      setArchiving(false);
    }
  };

  if (!canManage) {
    return <div className="p-6 text-center text-gray-600">لا تملك صلاحية الوصول لإدارة الخدمات الهرمية</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🗂️ إدارة الخدمات الهرمية (موسم جديد)</h1>
          <p className="text-sm text-gray-600 mt-1">الخدمة الأم ← الفنادق ← أنواع الغرف + الإعاشة</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => openServiceDialog()} className="bg-brand-600 hover:bg-brand-700" data-testid="add-hservice-btn">
            <Plus className="h-4 w-4 ml-2" /> خدمة جديدة
          </Button>
          {(user?.role === 'super_admin') && (
            <Button variant="outline" onClick={() => setArchiveDialog(true)} className="text-orange-600 border-orange-300" data-testid="archive-season-btn">
              <Archive className="h-4 w-4 ml-2" /> أرشفة الموسم
            </Button>
          )}
        </div>
      </div>

      {/* Services Tree */}
      {loading ? (
        <div className="text-center py-10 text-gray-500">جاري التحميل...</div>
      ) : services.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-600 mb-2">لا توجد خدمات بعد</p>
            <p className="text-sm text-gray-500">ابدأ بإنشاء خدمة جديدة (مثلاً: عمرة رجب 2026)</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {services.map(svc => (
            <Card key={svc.id} className="border-r-4 border-r-brand-500">
              <CardContent className="p-0">
                {/* Service Row */}
                <div className="flex items-center p-4 hover:bg-gray-50 cursor-pointer" onClick={() => toggleService(svc.id)}>
                  {expanded[svc.id] ? <ChevronDown className="h-5 w-5 text-brand-600" /> : <ChevronRight className="h-5 w-5 text-brand-600" />}
                  <div className="flex-1 mr-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-lg text-gray-900">{svc.name}</h3>
                      <Badge variant="secondary">{svc.service_type}</Badge>
                      {svc.departure_date && <Badge variant="outline" className="text-xs"><Calendar className="h-3 w-3 ml-1" />{svc.departure_date}</Badge>}
                      {/* 🆕 Origin badge */}
                      {svc.origin === 'supplier' ? (
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs">📦 مورد: {suppliers.find(s => s.id === svc.supplier_id)?.name || 'غير معروف'}</Badge>
                      ) : (
                        <Badge className="bg-green-50 text-green-700 border-green-200 text-xs">🏢 صنهاجة</Badge>
                      )}
                    </div>
                    {svc.description && <p className="text-sm text-gray-500 mt-1">{svc.description}</p>}
                  </div>
                  <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                    <Button size="sm" variant="outline" onClick={() => openHotelDialog(svc.id)} className="bg-green-50 hover:bg-green-100 border-green-300 text-green-700" data-testid="add-hotel-btn">
                      <Plus className="h-4 w-4 ml-1" /> فندق
                    </Button>
                    {canEditDelete && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openServiceDialog(svc)}><Edit className="h-4 w-4 text-brand-600" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteService(svc.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Hotels */}
                {expanded[svc.id] && (
                  <div className="bg-brand-50/30 border-t">
                    {(hotels[svc.id] || []).length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        لا توجد فنادق. 
                        <Button size="sm" variant="link" onClick={() => openHotelDialog(svc.id)}>إضافة فندق</Button>
                      </div>
                    ) : (
                      (hotels[svc.id] || []).map(h => (
                        <div key={h.id}>
                          <div className="flex items-center p-3 pr-10 hover:bg-white cursor-pointer border-t border-brand-100" onClick={() => toggleHotel(h.id)}>
                            {expanded[h.id] ? <ChevronDown className="h-4 w-4 text-green-600" /> : <ChevronRight className="h-4 w-4 text-green-600" />}
                            <Building2 className="h-4 w-4 text-green-600 mr-2" />
                            <div className="flex-1 mr-2">
                              <span className="font-medium">{h.name}</span>
                              {h.city && <Badge variant="outline" className="text-xs mr-2">{h.city}</Badge>}
                            </div>
                            <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                              <Button size="sm" variant="outline" onClick={() => openRoomDialog(h.id)} className="bg-purple-50 hover:bg-purple-100 border-purple-300 text-purple-700 h-7 px-2 text-xs" data-testid="add-room-btn">
                                <Plus className="h-3 w-3 ml-1" /> غرفة
                              </Button>
                              {canEditDelete && (
                                <>
                                  <Button size="sm" variant="ghost" onClick={() => openHotelDialog(svc.id, h)}><Edit className="h-3.5 w-3.5 text-brand-600" /></Button>
                                  <Button size="sm" variant="ghost" onClick={() => deleteHotel(svc.id, h.id)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Rooms */}
                          {expanded[h.id] && (
                            <div className="bg-green-50/30 border-t border-green-100 pr-16">
                              {(rooms[h.id] || []).length === 0 ? (
                                <div className="p-3 text-center text-xs text-gray-500">
                                  لا توجد غرف. 
                                  <Button size="sm" variant="link" onClick={() => openRoomDialog(h.id)}>إضافة غرفة</Button>
                                </div>
                              ) : (
                                <table className="w-full text-sm">
                                  <thead className="bg-green-100/50">
                                    <tr>
                                      <th className="p-2 text-right">نوع الغرفة</th>
                                      <th className="p-2 text-right">السعر</th>
                                      <th className="p-2 text-right">سعر الإعاشة</th>
                                      <th className="p-2 text-right">المجموع (مع إعاشة)</th>
                                      <th className="p-2 text-right">إجراءات</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(rooms[h.id] || []).map(r => (
                                      <tr key={r.id} className="border-t border-green-100 hover:bg-white">
                                        <td className="p-2"><BedDouble className="h-3.5 w-3.5 text-green-600 inline ml-1" />{r.room_type}</td>
                                        <td className="p-2 font-mono">{r.price?.toLocaleString()} دج</td>
                                        <td className="p-2 font-mono text-gray-600">{r.meal_price ? `+${r.meal_price.toLocaleString()} دج` : '-'}</td>
                                        <td className="p-2 font-mono font-bold text-green-700">{((r.price || 0) + (r.meal_price || 0)).toLocaleString()} دج</td>
                                        <td className="p-2">
                                          <div className="flex gap-1">
                                            {canEditDelete && (
                                              <>
                                                <Button size="sm" variant="ghost" onClick={() => openRoomDialog(h.id, r)}><Edit className="h-3 w-3 text-brand-600" /></Button>
                                                <Button size="sm" variant="ghost" onClick={() => deleteRoom(h.id, r.id)}><Trash2 className="h-3 w-3 text-red-600" /></Button>
                                              </>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ===== Service Dialog ===== */}
      <Dialog open={serviceDialog.open} onOpenChange={o => setServiceDialog(s => ({ ...s, open: o }))}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{serviceDialog.editing ? 'تعديل' : 'إضافة'} خدمة</DialogTitle>
            <DialogDescription>أدخل بيانات الخدمة الأم (العنوان + التاريخ)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>اسم الخدمة *</Label>
              <Input value={sForm.name} onChange={e => setSForm({ ...sForm, name: e.target.value })} placeholder="مثال: عمرة رجب 2026" />
            </div>
            <div>
              <Label>نوع الخدمة</Label>
              <Select value={sForm.service_type} onValueChange={v => setSForm({ ...sForm, service_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="عمرة">عمرة</SelectItem>
                  <SelectItem value="حج">حج</SelectItem>
                  <SelectItem value="رحلة منظمة">رحلة منظمة</SelectItem>
                  <SelectItem value="أخرى">أخرى</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>تاريخ المغادرة</Label>
                <Input type="date" value={sForm.departure_date} onChange={e => setSForm({ ...sForm, departure_date: e.target.value })} />
              </div>
              <div>
                <Label>تاريخ العودة</Label>
                <Input type="date" value={sForm.return_date} onChange={e => setSForm({ ...sForm, return_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>الوصف (اختياري)</Label>
              <Input value={sForm.description} onChange={e => setSForm({ ...sForm, description: e.target.value })} />
            </div>

            {/* 🆕 Service origin (internal / supplier) */}
            <div className="rounded-lg border border-yellow-200 bg-yellow-50/40 p-3 space-y-2" data-testid="hservice-origin-block">
              <Label className="text-sm font-bold text-yellow-800">مصدر الخدمة</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSForm({ ...sForm, origin: 'internal', supplier_id: '', supplier_cost: '' })}
                  className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${sForm.origin === 'internal' ? 'bg-green-100 border-green-400 text-green-800 font-bold' : 'bg-white border-gray-200 text-gray-600'}`}
                >🏢 خدمة صنهاجة</button>
                <button
                  type="button"
                  onClick={() => setSForm({ ...sForm, origin: 'supplier' })}
                  className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${sForm.origin === 'supplier' ? 'bg-yellow-100 border-yellow-500 text-yellow-800 font-bold' : 'bg-white border-gray-200 text-gray-600'}`}
                >📦 خدمة مورد</button>
              </div>
              {sForm.origin === 'supplier' && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div>
                    <Label className="text-xs">المورد *</Label>
                    <select
                      value={sForm.supplier_id}
                      onChange={e => setSForm({ ...sForm, supplier_id: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm"
                      data-testid="hservice-supplier-select"
                    >
                      <option value="">اختر مورداً...</option>
                      {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">تكلفة المورد (دج)</Label>
                    <Input type="number" value={sForm.supplier_cost} onChange={e => setSForm({ ...sForm, supplier_cost: e.target.value })} placeholder="ما تدفعه الوكالة للمورد" />
                  </div>
                </div>
              )}
            </div>

            {/* 🆕 Age-based pricing preset (per service) — configured ONCE by
                the manager / accountant / services_manager. Regular staff will
                see these prices auto-loaded (read-only) on the trip form. */}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="hservice-age-pricing-block">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-bold text-emerald-800">أسعار الشرائح العمرية (اختياري)</Label>
                <label className="flex items-center gap-2 text-xs text-emerald-700">
                  <input
                    type="checkbox"
                    checked={!!sForm.age_pricing_enabled}
                    onChange={e => setSForm({ ...sForm, age_pricing_enabled: e.target.checked, age_pricing: e.target.checked && !sForm.age_pricing ? emptyAgePricing() : sForm.age_pricing })}
                    data-testid="hservice-age-pricing-enable"
                  />
                  تفعيل التسعير حسب العمر
                </label>
              </div>
              {sForm.age_pricing_enabled && (
                <div className="pt-1">
                  <p className="text-[11px] text-emerald-700 mb-1">
                    اضبط سعر البيع{sForm.origin === 'supplier' ? ' وتكلفة المورد' : ''} لكل شريحة عمرية. عندما ينشئ الموظف رحلة بهذه الخدمة ويختار "حسب الأعمار"، ستُعبَّأ هذه الأسعار تلقائياً.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border border-emerald-200 rounded-md bg-white">
                      <thead className="bg-emerald-100 text-emerald-800">
                        <tr>
                          <th className="text-right px-2 py-1">الشريحة</th>
                          <th className="text-right px-2 py-1">سعر البيع (دج)</th>
                          {sForm.origin === 'supplier' && (
                            <th className="text-right px-2 py-1">تكلفة المورد (دج)</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {AGE_TIERS.map(tier => (
                          <tr key={tier} className="border-t border-emerald-100">
                            <td className="px-2 py-1 whitespace-nowrap">
                              {AGE_TIER_LABEL[tier].label}
                              <div className="text-[10px] text-gray-400">{AGE_TIER_LABEL[tier].range}</div>
                            </td>
                            <td className="px-2 py-1">
                              <Input
                                type="number"
                                value={sForm.age_pricing?.[tier]?.sell ?? ''}
                                onChange={e => setSForm({
                                  ...sForm,
                                  age_pricing: { ...(sForm.age_pricing || emptyAgePricing()), [tier]: { ...(sForm.age_pricing?.[tier] || {}), sell: e.target.value } }
                                })}
                                placeholder={tier === 'infant' || tier === 'young_child_no_seat' ? '0 (مجاناً)' : 'السعر'}
                                className="h-7 text-xs"
                                data-testid={`hservice-age-${tier}-sell`}
                              />
                            </td>
                            {sForm.origin === 'supplier' && (
                              <td className="px-2 py-1">
                                <Input
                                  type="number"
                                  value={sForm.age_pricing?.[tier]?.cost ?? ''}
                                  onChange={e => setSForm({
                                    ...sForm,
                                    age_pricing: { ...(sForm.age_pricing || emptyAgePricing()), [tier]: { ...(sForm.age_pricing?.[tier] || {}), cost: e.target.value } }
                                  })}
                                  placeholder={tier === 'infant' || tier === 'young_child_no_seat' ? '0' : 'التكلفة'}
                                  className="h-7 text-xs"
                                  data-testid={`hservice-age-${tier}-cost`}
                                />
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setServiceDialog({ open: false, editing: null })}>إلغاء</Button>
              <Button onClick={saveService} className="bg-brand-600 hover:bg-brand-700" data-testid="save-hservice-btn">حفظ</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Hotel Dialog ===== */}
      <Dialog open={hotelDialog.open} onOpenChange={o => setHotelDialog(s => ({ ...s, open: o }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{hotelDialog.editing ? 'تعديل' : 'إضافة'} فندق</DialogTitle>
            <DialogDescription>أدخل معلومات الفندق المرتبط بالخدمة</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>اسم الفندق *</Label>
              <Input value={hForm.name} onChange={e => setHForm({ ...hForm, name: e.target.value })} placeholder="مثال: فندق الفيرمونت" />
            </div>
            <div>
              <Label>المدينة</Label>
              <Input value={hForm.city} onChange={e => setHForm({ ...hForm, city: e.target.value })} placeholder="مكة / المدينة / إسطنبول ..." />
            </div>
            <div>
              <Label>ملاحظات</Label>
              <Input value={hForm.notes} onChange={e => setHForm({ ...hForm, notes: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setHotelDialog({ open: false, serviceId: null, editing: null })}>إلغاء</Button>
              <Button onClick={saveHotel} className="bg-green-600 hover:bg-green-700">حفظ</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Room Dialog ===== */}
      <Dialog open={roomDialog.open} onOpenChange={o => setRoomDialog(s => ({ ...s, open: o }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{roomDialog.editing ? 'تعديل' : 'إضافة'} غرفة</DialogTitle>
            <DialogDescription>حدد نوع الغرفة، سعرها، وسعر الإعاشة (اختياري)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>نوع الغرفة *</Label>
              <Select value={rForm.room_type} onValueChange={v => setRForm({ ...rForm, room_type: v })}>
                <SelectTrigger><SelectValue placeholder="اختر نوع الغرفة" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="فردية">فردية</SelectItem>
                  <SelectItem value="مزدوجة">مزدوجة</SelectItem>
                  <SelectItem value="ثلاثية">ثلاثية</SelectItem>
                  <SelectItem value="رباعية">رباعية</SelectItem>
                  <SelectItem value="خماسية">خماسية</SelectItem>
                  {/* 🆕 أنواع غرف خاصة بالفئات الإضافية — كل نوع يأخذ سعره المنفصل */}
                  <SelectItem value="طفل بسرير">🧒 طفل بسرير</SelectItem>
                  <SelectItem value="طفل بدون سرير">🧒 طفل بدون سرير</SelectItem>
                  <SelectItem value="رضيع">👶 رضيع</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>سعر الغرفة (دج) *</Label>
              <Input type="number" value={rForm.price} onChange={e => setRForm({ ...rForm, price: e.target.value })} placeholder="250000" />
            </div>
            <div>
              <Label>سعر الإعاشة لهذه الغرفة (اختياري)</Label>
              <Input type="number" value={rForm.meal_price} onChange={e => setRForm({ ...rForm, meal_price: e.target.value })} placeholder="0 أو 15000" />
              <p className="text-xs text-gray-500 mt-1">يُضاف تلقائياً عند اختيار "مع إعاشة" في الحجز</p>
            </div>
            <div>
              <Label>ملاحظات</Label>
              <Input value={rForm.notes} onChange={e => setRForm({ ...rForm, notes: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setRoomDialog({ open: false, hotelId: null, editing: null })}>إلغاء</Button>
              <Button onClick={saveRoom} className="bg-green-600 hover:bg-green-700">حفظ</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Archive Dialog ===== */}
      <Dialog open={archiveDialog} onOpenChange={setArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" /> أرشفة الموسم
            </DialogTitle>
            <DialogDescription>نقل جميع البيانات النشطة إلى الأرشيف وبدء موسم جديد</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm">
              <p className="font-semibold text-orange-800 mb-2">⚠️ هذه العملية ستقوم بـ:</p>
              <ul className="list-disc pr-5 space-y-1 text-orange-700">
                <li>نقل جميع الخدمات الحالية إلى الأرشيف</li>
                <li>نقل جميع الفنادق والغرف إلى الأرشيف</li>
                <li>نقل جميع الرحلات المنظمة الحالية إلى الأرشيف</li>
                <li>تفريغ الجداول النشطة لبدء موسم جديد</li>
              </ul>
              <p className="mt-2 text-orange-800 font-medium">الحجوزات القديمة ستبقى متاحة في قسم الأرشيف للعرض فقط.</p>
            </div>
            <div>
              <Label>تسمية الموسم (اختياري)</Label>
              <Input value={archiveLabel} onChange={e => setArchiveLabel(e.target.value)} placeholder="موسم عمرة 2025" />
              <p className="text-xs text-gray-500 mt-1">إذا تُركت فارغة سيتم استخدام الطابع الزمني تلقائياً</p>
            </div>
            <div className="bg-brand-50 border border-brand-200 rounded p-3">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="keep-unpaid-checkbox-wrap">
                <input
                  type="checkbox"
                  checked={keepUnpaid}
                  onChange={e => setKeepUnpaid(e.target.checked)}
                  className="mt-1 h-4 w-4 cursor-pointer"
                  data-testid="keep-unpaid-checkbox"
                />
                <div>
                  <div className="font-semibold text-brand-800 text-sm">📌 نقل الحجوزات غير المدفوعة للموسم الجديد</div>
                  <p className="text-xs text-brand-700 mt-1">
                    عند التفعيل: ستبقى الحجوزات التي لم يتم دفعها بعد (<span className="font-semibold">unpaid</span>) في الموسم الجديد لمتابعة تحصيلها، بينما يتم أرشفة الحجوزات المدفوعة كلياً أو جزئياً فقط.
                  </p>
                </div>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setArchiveDialog(false)} disabled={archiving}>إلغاء</Button>
              <Button onClick={runArchive} disabled={archiving} className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-archive-btn">
                <Archive className="h-4 w-4 ml-2" /> {archiving ? 'جاري الأرشفة...' : 'أرشف الآن'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HierarchicalServicesManagement;
